// components/video/ProjectOverlaySelector.tsx
import FeatherIcon from 'feather-icons-react';
import React, { useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { videoProjectService } from 'services/videoService';

interface Project {
  id: number;
  name: string;
  description?: string;
  created_at?: string;
}

interface ProjectOverlaySelectorProps {
  currentProjectId: number;
  selectedProjectIds: number[];
  onProjectsChange: (projectIds: number[]) => void;
  proximityRadius: number;
  onProximityRadiusChange: (radius: number) => void;
  showProximityRadius?: boolean; // ✅ NOVO: controlar se mostra ou não
}

const ProjectOverlaySelector: React.FC<ProjectOverlaySelectorProps> = ({
  currentProjectId,
  selectedProjectIds,
  onProjectsChange,
  proximityRadius,
  onProximityRadiusChange,
  showProximityRadius = true // ✅ NOVO: padrão true para manter compatibilidade
}) => {
  const [showModal, setShowModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (showModal) {
      loadProjects();
    }
  }, [showModal]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await videoProjectService.getAll();
      // Filtrar o projeto atual
      const otherProjects = response.filter(
        (p: Project) => p.id !== currentProjectId
      );
      setProjects(otherProjects);
    } catch (error) {
      console.error('[ProjectOverlay] Erro ao carregar projetos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProject = (projectId: number) => {
    if (selectedProjectIds.includes(projectId)) {
      onProjectsChange(selectedProjectIds.filter(id => id !== projectId));
    } else {
      onProjectsChange([...selectedProjectIds, projectId]);
    }
  };

  const handleSelectAll = () => {
    const filteredIds = filteredProjects.map(p => p.id);
    onProjectsChange(filteredIds);
  };

  const handleClearAll = () => {
    onProjectsChange([]);
  };

  const filteredProjects = projects.filter(
    project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCount = selectedProjectIds.length;

  return (
    <>
      {/* Botão para abrir o modal */}
      <Button
        variant={selectedCount > 0 ? 'info' : 'outline-secondary'}
        size="sm"
        onClick={() => setShowModal(true)}
        title="Ver sinalizações de outros projetos"
        style={{
          fontSize: '11px',
          padding: '4px 8px',
          fontWeight: selectedCount > 0 ? '600' : '500'
        }}
      >
        <FeatherIcon icon="layers" size={12} className="me-1" />
        Projetos
        {selectedCount > 0 && (
          <Badge bg="light" text="dark" className="ms-1">
            {selectedCount}
          </Badge>
        )}
      </Button>

      {/* Modal de seleção */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FeatherIcon icon="layers" size={20} className="me-2" />
            Sobrepor Sinalizações de Outros Projetos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Configuração de raio de proximidade - ✅ CONDICIONAL */}
          {showProximityRadius && (
            <div className="mb-3 p-3 bg-light rounded">
              <Form.Label className="fw-bold mb-2">
                <FeatherIcon icon="crosshair" size={16} className="me-2" />
                Raio de Proximidade GPS
                <Badge bg="warning" text="dark" className="ms-2">
                  Em Desenvolvimento
                </Badge>
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Range
                  min={5}
                  max={100}
                  step={5}
                  value={proximityRadius}
                  onChange={e =>
                    onProximityRadiusChange(Number(e.target.value))
                  }
                  style={{ flex: 1 }}
                />
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="number"
                    min={5}
                    max={100}
                    step={5}
                    value={proximityRadius}
                    onChange={e =>
                      onProximityRadiusChange(Number(e.target.value))
                    }
                    style={{ width: '80px' }}
                  />
                  <span className="text-muted">metros</span>
                </div>
              </div>
              <Form.Text className="text-muted">
                Mostrar apenas sinalizações dentro deste raio em relação às
                posições GPS do projeto atual
              </Form.Text>
            </div>
          )}

          {/* Barra de pesquisa */}
          <div className="mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <FeatherIcon icon="search" size={16} />
              </span>
              <Form.Control
                type="text"
                placeholder="Pesquisar projetos..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                >
                  <FeatherIcon icon="x" size={16} />
                </Button>
              )}
            </div>
          </div>

          {/* Botões de ação rápida */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <small className="text-muted">
                {selectedCount} de {filteredProjects.length} projetos
                selecionados
              </small>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSelectAll}
                disabled={filteredProjects.length === 0}
              >
                Selecionar Todos
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleClearAll}
                disabled={selectedCount === 0}
              >
                Limpar Seleção
              </Button>
            </div>
          </div>

          {/* Lista de projetos */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Carregando projetos...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FeatherIcon icon="inbox" size={48} className="mb-3" />
              <p>
                {searchTerm
                  ? 'Nenhum projeto encontrado com esse termo'
                  : 'Nenhum outro projeto disponível'}
              </p>
            </div>
          ) : (
            <div
              style={{
                maxHeight: '250px', // ✅ MUDADO: de 400px para 250px
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}
            >
              {filteredProjects.map(project => (
                <div
                  key={project.id}
                  className={`p-3 border-bottom ${
                    selectedProjectIds.includes(project.id)
                      ? 'bg-primary bg-opacity-10'
                      : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleToggleProject(project.id)}
                >
                  <Form.Check
                    type="checkbox"
                    id={`project-${project.id}`}
                    checked={selectedProjectIds.includes(project.id)}
                    onChange={() => handleToggleProject(project.id)}
                    label={
                      <div>
                        <div className="fw-bold">{project.name}</div>
                        {project.description && (
                          <small className="text-muted">
                            {project.description}
                          </small>
                        )}
                        {project.created_at && (
                          <div>
                            <small className="text-muted">
                              Criado em:{' '}
                              {new Date(
                                project.created_at
                              ).toLocaleDateString()}
                            </small>
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowModal(false)}
            disabled={selectedCount === 0}
          >
            <FeatherIcon icon="check" size={16} className="me-1" />
            Aplicar ({selectedCount})
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProjectOverlaySelector;
